/**
 * Building Geometry Merger
 *
 * Post-processes a building THREE.Group (output of any orchestrator) to merge
 * all same-material geometries into single draw calls.
 *
 * A typical 10-unit building: ~90 meshes → ~4 merged meshes (one per material type).
 * This reduces draw calls by ~95% per building.
 *
 * IMPORTANT: Merge happens in building-local space. The building group's own
 * position/rotation is preserved and NOT baked into geometry. This ensures
 * the building can still be repositioned/rotated after merging.
 */

import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'

/**
 * Merge all child geometries in a building group by material reference.
 *
 * @param buildingGroup - Output from an orchestrator (SingleRow, DoubleRow, etc.)
 * @param enableShadows - Whether merged meshes should cast/receive shadows
 * @returns New THREE.Group with merged geometry (same name, userData, position, rotation)
 */
export function mergeBuildingGeometries(
  buildingGroup: THREE.Group,
  enableShadows: boolean = true
): THREE.Group {
  // Collect leaf meshes grouped by material reference (shared materials = same key)
  const materialGroups = new Map<THREE.Material, THREE.BufferGeometry[]>()

  // Save the building group's world-level transforms
  const savedPosition = buildingGroup.position.clone()
  const savedRotation = buildingGroup.rotation.clone()
  const savedScale = buildingGroup.scale.clone()

  // Temporarily reset building-level transforms so updateMatrixWorld
  // gives us geometry in building-local coordinates
  buildingGroup.position.set(0, 0, 0)
  buildingGroup.rotation.set(0, 0, 0)
  buildingGroup.scale.set(1, 1, 1)
  buildingGroup.updateMatrixWorld(true)

  // Collect LineSegments separately (border lines for outdoor areas)
  const lineSegmentsList: THREE.LineSegments[] = []

  // Collect "roof"-named groups separately — they need to stay as a distinct
  // group so the global showRoofs toggle can set .visible on them.
  const roofGroups: THREE.Group[] = []
  const roofAncestors = new Set<THREE.Object3D>()

  // First pass: find roof groups and mark their descendants to skip during merge
  buildingGroup.traverse((child) => {
    if (child.name === 'roof' && child instanceof THREE.Group) {
      roofGroups.push(child)
      // Mark all descendants of this roof group
      child.traverse((desc) => roofAncestors.add(desc))
    }
  })

  buildingGroup.traverse((child) => {
    // Skip anything inside a roof group — handled separately
    if (roofAncestors.has(child)) return

    // Preserve LineSegments (border lines) — can't merge with mesh geometries
    if (child instanceof THREE.LineSegments) {
      const cloned = child.clone()
      cloned.applyMatrix4(child.matrixWorld)
      lineSegmentsList.push(cloned)
      return
    }

    if (!(child instanceof THREE.Mesh)) return
    if (!child.geometry) return

    const mat = child.material as THREE.Material
    if (!materialGroups.has(mat)) {
      materialGroups.set(mat, [])
    }

    // Clone geometry and bake the mesh's world matrix into it
    // (world matrix is now building-local since we zeroed the building transform)
    const geo = child.geometry.clone()
    geo.applyMatrix4(child.matrixWorld)
    materialGroups.get(mat)!.push(geo)
  })

  // Restore building-level transforms
  buildingGroup.position.copy(savedPosition)
  buildingGroup.rotation.copy(savedRotation)
  buildingGroup.scale.copy(savedScale)

  // Build merged group
  const merged = new THREE.Group()
  merged.name = buildingGroup.name
  merged.userData = { ...buildingGroup.userData }

  for (const [material, geometries] of materialGroups) {
    if (geometries.length === 0) continue

    const mergedGeo = mergeGeometries(geometries, false)
    if (!mergedGeo) {
      // Fallback: if merge fails (mismatched attributes), add individual meshes
      console.warn(`mergeBuildingGeometries: merge failed for material, adding ${geometries.length} individual meshes`)
      geometries.forEach((geo) => {
        const mesh = new THREE.Mesh(geo, material)
        mesh.castShadow = enableShadows && !(material instanceof THREE.MeshBasicMaterial)
        mesh.receiveShadow = enableShadows
        merged.add(mesh)
      })
      continue
    }

    // Eagerly compute bounding sphere for frustum culling
    // (Mapbox custom layer uses a non-standard camera setup; explicit bounds ensure culling works)
    mergedGeo.computeBoundingSphere()

    const mesh = new THREE.Mesh(mergedGeo, material)
    // Doors (MeshBasicMaterial) don't cast shadows; walls/floors do
    mesh.castShadow = enableShadows && !(material instanceof THREE.MeshBasicMaterial)
    mesh.receiveShadow = enableShadows
    merged.add(mesh)

    // Dispose temp cloned geometries (merged geo is a new allocation)
    geometries.forEach((g) => g.dispose())
  }

  // Add preserved LineSegments (border lines for outdoor areas)
  for (const lineSegments of lineSegmentsList) {
    merged.add(lineSegments)
  }

  // Add preserved roof groups — bake their world matrices into geometry
  // but keep them as named groups so setRoofVisibility can toggle them
  for (const roofGroup of roofGroups) {
    const roofClone = new THREE.Group()
    roofClone.name = 'roof'

    // Merge roof meshes by material (same optimization as the rest)
    const roofMaterialGroups = new Map<THREE.Material, THREE.BufferGeometry[]>()
    roofGroup.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.geometry) return
      const mat = child.material as THREE.Material
      if (!roofMaterialGroups.has(mat)) roofMaterialGroups.set(mat, [])
      const geo = child.geometry.clone()
      geo.applyMatrix4(child.matrixWorld)
      roofMaterialGroups.get(mat)!.push(geo)
    })

    for (const [material, geometries] of roofMaterialGroups) {
      if (geometries.length === 0) continue
      const mergedGeo = mergeGeometries(geometries, false)
      if (mergedGeo) {
        mergedGeo.computeBoundingSphere()
        const roofMat = material.clone()
        roofMat.transparent = true
        roofMat.opacity = 0.5
        roofMat.depthWrite = false
        const mesh = new THREE.Mesh(mergedGeo, roofMat)
        mesh.castShadow = enableShadows && !(material instanceof THREE.MeshBasicMaterial)
        mesh.receiveShadow = enableShadows
        roofClone.add(mesh)
        geometries.forEach((g) => g.dispose())
      }
    }

    merged.add(roofClone)
  }

  // Restore building-level position/rotation/scale on the merged group
  merged.position.copy(savedPosition)
  merged.rotation.copy(savedRotation)
  merged.scale.copy(savedScale)

  return merged
}
